import React from 'react';
import { DocumentationTypo } from './DocumentationTypo';
import { Note } from '../Note/Note';
import { DevPreviewBanner } from './DevPreviewBanner';

const { Paragraph, H1, H2, CodeBlock, CodeBlockInline, DocPage } = DocumentationTypo;

const displayDevPreviewWarnings = __appConfig.app.displayDevPreviewWarnings;

export const StateDocPage: React.FC = () => {
    return (
        <DocPage>
            <article>
                <H1>Хранение состояния</H1>
                {displayDevPreviewWarnings && <DevPreviewBanner />}
                <Paragraph>
                    API Яндекс Диалогов позволяет сохранять данные внутри сессии навыка, а&nbsp;если пользователь
                    авторизован в&nbsp;Яндексе&nbsp;&mdash; то&nbsp;и&nbsp;между сессиями.
                </Paragraph>
                <section>
                    <H2>Хранение состояния сессии</H2>
                    <Paragraph>
                        Чтобы сохранить данные внутри сессии, навык должен отправить JSON-объект{' '}
                        <CodeBlockInline>session_state</CodeBlockInline> в&nbsp;ответе:
                    </Paragraph>
                    <Paragraph>
                        <CodeBlock type="json">
                            {`{
  "response": {
    "text": "Здравствуйте! Это мы, хороводоведы.",
    "tts": "Здравствуйте! Это мы, хоров+одо в+еды.",
    "end_session": false
  },
  "session_state": {
      "value": 10
  },
  "version": "1.0"
}`}
                        </CodeBlock>
                    </Paragraph>
                    <Paragraph>
                        <Note
                            type="info"
                            inlineContent={<>Максимальный размер состояния сессии&nbsp;&mdash; 1&nbsp;килобайт.</>}
                        />
                    </Paragraph>
                    <Paragraph>
                        <Note
                            type="warning"
                            inlineContent={
                                <>
                                    Состояние сессии пропадет, если в&nbsp;ответе навыка не&nbsp;вернуть{' '}
                                    <CodeBlockInline>session_state</CodeBlockInline>, поэтому, для сохранения состояния
                                    между запросами, нужно возвращать <CodeBlockInline>session_state</CodeBlockInline>{' '}
                                    даже при отсутствии изменений.
                                </>
                            }
                        />
                    </Paragraph>
                    <Paragraph>Записанное значение придёт в&nbsp;следующем запросе в&nbsp;навык:</Paragraph>
                    <Paragraph>
                        <CodeBlock type="json">
                            {`{
  "meta": {
    "locale": "ru-RU",
    "timezone": "Europe/Moscow",
    "client_id": "ru.yandex.searchplugin/5.80 (Samsung Galaxy; Android 4.4)",
    "interfaces": {
      "screen": { }
    }
  },
  "request": {
    "command": "привет",
    "original_utterance": "привет",
    "type": "SimpleUtterance",
    "markup": {
      "dangerous_context": true
    },
    "payload": {},
    "nlu": {
      "tokens": [
         "привет"
      ],
      "entities": [
      ]
    }
  },
  "session": {
    "new": true,
    "message_id": 4,
    "session_id": "2eac4854-fce721f3-b845abba-20d60",
    "skill_id": "3ad36498-f5rd-4079-a14b-788652932056",
    "user_id": "AC9WC3DF6FCE052E45A4566A48E6B7193774B84814CE49A922E163B8B29881DC"
    "application": {
      "application_id": "AC9WC3DF6FCE052E45A4566A48E6B7193774B84814CE49A922E163B8B29881DC"
    },
  },
  "state": {
    "session": {
      "value": 10
    }
  },
  "version": "1.0"
}`}
                        </CodeBlock>
                    </Paragraph>
                </section>
                <section>
                    <H2>Хранение состояния пользователя между сессиями</H2>
                    <Note
                        type="info"
                        inlineContent={
                            <>
                                Хранение пользовательского состояния доступно только для пользователей навыка,
                                авторизованных в&nbsp;Яндексе
                            </>
                        }
                    />
                    <Paragraph>
                        Чтобы сохранить данные о&nbsp;пользователе, навык должен отправить JSON-объект{' '}
                        <CodeBlockInline>user_state_update</CodeBlockInline> в&nbsp;ответе:
                    </Paragraph>
                    <Paragraph>
                        <CodeBlock type="json">
                            {`{
  "response": {
    "text": "Здравствуйте! Это мы, хороводоведы.",
    "tts": "Здравствуйте! Это мы, хоров+одо в+еды.",
    "end_session": false
  },
  "session": {
    "session_id": "2eac4854-fce721f3-b845abba-20d60",
    "message_id": 4,
    "user_id": "AC9WC3DF6FCE052E45A4566A48E6B7193774B84814CE49A922E163B8B29881DC"
    "application": {
      "application_id": "AC9WC3DF6FCE052E45A4566A48E6B7193774B84814CE49A922E163B8B29881DC"
    },
  },
  "user_state_update": {
      "value": 42
  },
  "version": "1.0"
}`}
                        </CodeBlock>
                    </Paragraph>
                    <Paragraph>Сохранённое значение придёт в&nbsp;следующем запросе в&nbsp;навык:</Paragraph>
                    <Paragraph>
                        <CodeBlock type="json">
                            {`{
  "meta": {
    "locale": "ru-RU",
    "timezone": "Europe/Moscow",
    "client_id": "ru.yandex.searchplugin/5.80 (Samsung Galaxy; Android 4.4)",
    "interfaces": {
      "screen": { }
    }
  },
  "request": {
    "command": "привет",
    "original_utterance": "привет",
    "type": "SimpleUtterance",
    "markup": {
      "dangerous_context": true
    },
    "payload": {},
    "nlu": {
      "tokens": [
         "привет"
      ],
      "entities": [
      ]
    }
  },
  "session": {
    "new": true,
    "message_id": 4,
    "session_id": "2eac4854-fce721f3-b845abba-20d60",
    "skill_id": "3ad36498-f5rd-4079-a14b-788652932056",
    "user_id": "AC9WC3DF6FCE052E45A4566A48E6B7193774B84814CE49A922E163B8B29881DC"
    "application": {
      "application_id": "AC9WC3DF6FCE052E45A4566A48E6B7193774B84814CE49A922E163B8B29881DC"
    },
    "user": {
      "user_id": "8D7196B4A8AA15CFF3B7B3046738C03F234A7E638FFE33B23F2350BBD940B644"
    }
  },
  "state": {
    "user": {
      "value": 42
    }
  },
  "version": "1.0"
}`}
                        </CodeBlock>
                    </Paragraph>
                    <Paragraph>
                        Для того чтобы удалить ранее записанное в&nbsp;профиль пользователя поле, навык должен отправить
                        это поле со&nbsp;значением <CodeBlockInline>null</CodeBlockInline>.
                    </Paragraph>
                    <Note
                        type="info"
                        inlineContent={<>Максимальный размер состояния пользователя&nbsp;&mdash; 1&nbsp;килобайт.</>}
                    />
                </section>
            </article>
        </DocPage>
    );
};
